import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * ARIA menu-button / listbox keyboard-nav for Bootstrap dropdowns.
 *
 * Takes over from Bootstrap 5's built-in arrow-key handler because Bootstrap
 * only walks `.dropdown-item` and doesn't handle Home/End, Enter/Space, Tab,
 * type-ahead, or nested `<ul><li>` structures. Watches the trigger's
 * `aria-expanded` for open/close, focuses first/current item on open, restores
 * focus on close. Returns callback refs so wiring re-runs when target DOM
 * elements attach/detach (they're rendered conditionally in most consumers).
 */

const DEFAULT_ITEM_SELECTOR =
  '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"], [role="option"], .dropdown-item, .available-country-btn';

const isPrintableChar = (key) => typeof key === 'string' && key.length === 1 && /\S/.test(key);

const focusItem = (item) => {
  if (!item) return;
  if (!item.hasAttribute('tabindex') && !item.matches('a[href], button, input, select, textarea')) item.setAttribute('tabindex', '-1');
  item.focus();
  // Bring the focused item into view — the search flow in some dropdowns
  // (SessionUsers) re-renders the list under focus and the newly-focused
  // item can land outside the scroll viewport, making the focus ring invisible.
  if (typeof item.scrollIntoView === 'function') {
    item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
};

const getItems = (menu, selector) =>
  menu ? Array.from(menu.querySelectorAll(selector)).filter((el) => !el.hasAttribute('disabled')) : [];

const getActiveItem = (items) => {
  const selected = items.find(
    (el) =>
      el.getAttribute('aria-selected') === 'true' ||
      el.getAttribute('aria-checked') === 'true' ||
      el.classList.contains('active')
  );
  return selected || items[0] || null;
};

const findByPrefix = (items, buffer, getText, startIndex) => {
  const query = buffer.toLowerCase();
  const total = items.length;
  for (let i = 1; i <= total; i += 1) {
    const idx = (startIndex + i) % total;
    const text = (getText(items[idx]) || items[idx].textContent || '').trim().toLowerCase();
    if (text.startsWith(query)) return items[idx];
  }
  return null;
};

const useMenuKeyboardNav = ({
  triggerRef: externalTriggerRef,
  menuRef: externalMenuRef,
  itemSelector = DEFAULT_ITEM_SELECTOR,
  typeAhead = true,
  typeAheadText,
  restoreFocusOnClose = true,
  // Standard ARIA menu-button pattern closes the menu on Tab and moves focus
  // out. Set to false for dropdowns that host additional focusable controls
  // (e.g. Select All checkbox, Clear button) inside the menu — Tab should walk
  // through those without closing.
  closeOnTab = true,
  // Popups that hold a search box plus a listbox of options (Host, Assignee,
  // user filter): one option is a Tab stop (roving tabindex), Tab moves
  // search -> list -> next control, and the popup closes once focus leaves it.
  listbox = false,
} = {}) => {
  // Track elements via state so the wiring effect re-runs when they attach/detach.
  const [triggerEl, setTriggerEl] = useState(externalTriggerRef?.current || null);
  const [menuEl, setMenuEl] = useState(externalMenuRef?.current || null);

  const setTrigger = useCallback((node) => {
    setTriggerEl(node);
    if (externalTriggerRef) externalTriggerRef.current = node;
  }, [externalTriggerRef]);

  const setMenu = useCallback((node) => {
    setMenuEl(node);
    if (externalMenuRef) externalMenuRef.current = node;
  }, [externalMenuRef]);

  const typeAheadBufferRef = useRef('');
  const typeAheadTimerRef = useRef(null);

  useEffect(() => {
    const trigger = triggerEl;
    const menu = menuEl;
    if (!trigger || !menu) return undefined;

    const focusOnOpen = () => {
      // Prefer focusing the search input (if any) before checking items — the
      // Country/State dropdown loads its list async, so on the very first open
      // items can be empty for a beat. Landing focus on the search input still
      // lets ArrowDown reach the menu's keydown handler once items arrive.
      const searchInput = menu.querySelector('input[type="text"], input:not([type])');
      if (searchInput) {
        searchInput.focus();
        return;
      }
      const items = getItems(menu, itemSelector);
      if (items.length === 0) return;
      focusItem(getActiveItem(items));
    };

    const closeMenu = (focusTrigger = true) => {
      if (trigger.getAttribute('aria-expanded') === 'true') {
        trigger.click();
      }
      if (focusTrigger && restoreFocusOnClose) trigger.focus();
    };

    const handleKeyDown = (event) => {
      const items = getItems(menu, itemSelector);
      const isInInput = document.activeElement && document.activeElement.tagName === 'INPUT';
      if (items.length === 0 && !['Escape', 'Tab'].includes(event.key)) return;
      const currentIndex = items.indexOf(document.activeElement);

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          // Stop Bootstrap 5's delegated dropdown handler (on document) from
          // running after ours — for dropdowns whose items aren't `.dropdown-item`
          // (e.g. CountryStateDropDown's `.available-country-btn`) Bootstrap
          // finds no items and its `_selectMenuItem` no-ops, but it still
          // calls `instance.show()` which can move focus back to the trigger.
          event.stopPropagation();
          if (isInInput && listbox && !document.activeElement.value) {
            focusItem(getActiveItem(items));
          } else if (isInInput || currentIndex < 0) {
            focusItem(items[0]);
          } else {
            focusItem(items[(currentIndex + 1) % items.length]);
          }
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          event.stopPropagation();
          if (isInInput) return;
          const prev = items[(currentIndex - 1 + items.length) % items.length] || items[items.length - 1];
          focusItem(prev);
          break;
        }
        case 'Home': {
          event.preventDefault();
          event.stopPropagation();
          focusItem(items[0]);
          break;
        }
        case 'End': {
          event.preventDefault();
          event.stopPropagation();
          focusItem(items[items.length - 1]);
          break;
        }
        case 'Enter':
        case ' ': {
          if (isInInput) return;
          if (currentIndex >= 0) {
            event.preventDefault();
            event.stopPropagation();
            const item = items[currentIndex];
            item.click();
            if (restoreFocusOnClose && document.activeElement === item && trigger.getAttribute('aria-expanded') !== 'true') {
              trigger.focus();
            }
          }
          break;
        }
        case 'Escape': {
          event.preventDefault();
          event.stopPropagation();
          closeMenu(true);
          break;
        }
        case 'Tab': {
          if (closeOnTab && !listbox) closeMenu(false);
          break;
        }
        default: {
          if (typeAhead && isPrintableChar(event.key) && !event.metaKey && !event.ctrlKey && !event.altKey) {
            if (isInInput) return;
            typeAheadBufferRef.current += event.key;
            if (typeAheadTimerRef.current) clearTimeout(typeAheadTimerRef.current);
            typeAheadTimerRef.current = setTimeout(() => {
              typeAheadBufferRef.current = '';
            }, 500);
            const startIndex = currentIndex >= 0 ? currentIndex : 0;
            const match = findByPrefix(
              items,
              typeAheadBufferRef.current,
              typeAheadText || ((el) => el.textContent),
              startIndex - 1
            );
            if (match) {
              event.preventDefault();
              event.stopPropagation();
              focusItem(match);
            }
          }
        }
      }
    };

    // Attach on `window` capture, not `document`. Bootstrap 5's dropdown
    // handler is also on `document` capture; if we shared it, Bootstrap fires
    // first and moves focus by one, then we move focus by one more — ArrowUp/
    // Down skip every other item. Window capture runs BEFORE document capture,
    // so our `stopPropagation()` blocks Bootstrap entirely.
    const scopedKeyDown = (event) => {
      if (!menu.contains(event.target)) return;
      handleKeyDown(event);
    };
    window.addEventListener('keydown', scopedKeyDown, true);

    const syncRovingTabStop = () => {
      const items = getItems(menu, itemSelector);
      if (items.length === 0) return;
      const stop = items.find((el) => el === document.activeElement) || getActiveItem(items);
      items.forEach((el) => el.setAttribute('tabindex', el === stop ? '0' : '-1'));
    };
    const closeWhenFocusLeaves = (event) => {
      const next = event.relatedTarget;
      if (!next || menu.contains(next) || trigger.contains(next) || next.contains(menu)) return;
      closeMenu(false);
    };
    const closesOnFocusLeave = listbox || !closeOnTab;
    let listObserver = null;
    if (listbox) {
      syncRovingTabStop();
      menu.addEventListener('focusin', syncRovingTabStop);
      listObserver = new MutationObserver(syncRovingTabStop);
      listObserver.observe(menu, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
    }
    if (closesOnFocusLeave) menu.addEventListener('focusout', closeWhenFocusLeaves);
    trigger.addEventListener('focusout', closeWhenFocusLeaves);

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.attributeName === 'aria-expanded') {
          const open = trigger.getAttribute('aria-expanded') === 'true';
          if (open) {
            setTimeout(focusOnOpen, 0);
          } else if (listbox) {
            const items = getItems(menu, itemSelector);
            const selected = getActiveItem(items);
            items.forEach((el) => el.setAttribute('tabindex', el === selected ? '0' : '-1'));
          }
        }
      }
    });
    observer.observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });

    return () => {
      window.removeEventListener('keydown', scopedKeyDown, true);
      if (listbox) {
        menu.removeEventListener('focusin', syncRovingTabStop);
        listObserver.disconnect();
      }
      if (closesOnFocusLeave) menu.removeEventListener('focusout', closeWhenFocusLeaves);
      trigger.removeEventListener('focusout', closeWhenFocusLeaves);
      observer.disconnect();
      if (typeAheadTimerRef.current) clearTimeout(typeAheadTimerRef.current);
    };
  }, [triggerEl, menuEl, itemSelector, typeAhead, typeAheadText, restoreFocusOnClose, closeOnTab, listbox]);

  return { triggerRef: setTrigger, menuRef: setMenu };
};

export default useMenuKeyboardNav;
